import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Stack,
  Chip,
  Divider,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItem,
  TextField,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  MoreVert as MoreVertIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon,
  MedicalServices as MedicalIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Mic as MicIcon
} from '@mui/icons-material';
import { Plus, PlusCircle, Microphone, User as UserIcon, File, Note, Users } from '@phosphor-icons/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useAppUser } from '@/contexts/AppUserProvider';
import { createPersonService } from '@/services/personService';
import { getAllTranscriptionSessions } from '@/services/transcriptionSessionService';
import sessionNavigationService from '@/services/sessionNavigationService';
import { createTranscriptionSession } from '@/services/transcriptionSessionService';
import { createSpeakerManagementService } from '@/services/speakerManagementService';
import * as templateService from '@/services/templateService';
import { Person } from '@/types/peopleTypes';
import { TranscriptionSession } from '@/types/transcriptionSessionTypes';
import { Template, GetAllTemplatesParams } from '@/types/templateTypes';
import { AssociatePersonToSessionRequest } from '@/types/speakerManagementTypes';
import { UpdatePersonInput } from '@/types/peopleTypes';
import AttioTable from '@/components/AttioTable/AttioTable';
import { UnifiedTabs, UnifiedTabsContainer } from '@/components/UnifiedTabs';
import { useToast } from '@/components/Toast';

// Fallback templates for when no real templates are available
const fallbackTemplatesForSelection = [
  {
    id: 'fallback-1',
    name: 'General Interview',
    description: 'A general interview template',
    icon: <DescriptionIcon />
  },
  {
    id: 'fallback-2',
    name: 'Basic Consultation',
    description: 'Basic consultation template',
    icon: <DescriptionIcon />
  }
];

// Helper function to get icon for template category
const getTemplateIcon = (categoryName: string): React.ReactElement => {
  const lowercaseName = categoryName.toLowerCase();

  if (lowercaseName.includes('medical') || lowercaseName.includes('health')) {
    return <MedicalIcon />;
  }
  if (lowercaseName.includes('education') || lowercaseName.includes('school')) {
    return <SchoolIcon />;
  }
  if (
    lowercaseName.includes('business') ||
    lowercaseName.includes('corporate')
  ) {
    return <BusinessIcon />;
  }

  return <DescriptionIcon />; // Default icon
};

// --- Session Creation with Person Association ---
interface CreateSessionWithPersonOptions {
  personId: string;
  templateId?: number;
  templateName?: string;
  userId: string;
  getAccessTokenSilently: any;
  getAccessTokenWithPopup: any;
  navigate: (path: string) => void;
  speakerManagementService: any;
}

const createSessionWithPerson = async (
  options: CreateSessionWithPersonOptions
): Promise<string | undefined> => {
  const {
    personId,
    templateId,
    templateName,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    navigate,
    speakerManagementService
  } = options;

  try {
    console.log(
      'Creating new session for person:',
      personId,
      'with template:',
      templateId,
      templateName
    );

    const sessionTitle = templateName
      ? `${templateName} Session - ${new Date().toLocaleString()}`
      : `Person Interview Session - ${new Date().toLocaleString()}`;

    // Create the session first
    const session = await createTranscriptionSession(
      {
        title: sessionTitle,
        templateId: templateId || null,
        sessionStartTime: new Date().toISOString()
      },
      userId,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const sessionId = session.id;

    if (!sessionId) {
      console.error('Failed to create session - no ID returned');
      return undefined;
    }

    console.log('Created session:', sessionId);

    // Associate the person with the session
    try {
      const associationRequest: AssociatePersonToSessionRequest = {
        personId: personId
        // Note: speakerIndex is optional and will be assigned during actual transcription
      };

      const associationSuccess =
        await speakerManagementService.associatePersonToSession(
          sessionId,
          associationRequest
        );

      if (!associationSuccess) {
        console.warn(
          'Failed to associate person with session, but continuing anyway'
        );
      } else {
        console.log(
          'Successfully associated person',
          personId,
          'with session',
          sessionId
        );
      }
    } catch (error) {
      console.error('Error associating person with session:', error);
      // Continue anyway - the session is created even if association fails
    }

    // Navigate to the session
    console.log('Navigating to /transcript/' + sessionId);
    navigate(`/transcript/${sessionId}`);
    return sessionId;
  } catch (error) {
    console.error('Failed to create session with person:', error);
    return undefined;
  }
};

// --- Select Template Dialog Component ---
interface SelectTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string | null, templateName: string | null) => void;
  templates: Array<{
    id: string;
    name: string;
    description: string;
    icon: React.ReactElement;
  }>;
  personName?: string;
}

const SelectTemplateDialog: React.FC<SelectTemplateDialogProps> = ({
  open,
  onClose,
  onSelect,
  templates,
  personName
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates;
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [templates, searchTerm]);

  const handleProceed = () => {
    if (selectedValue !== null) {
      const template = templates.find((t) => t.id === selectedValue);
      onSelect(selectedValue, template?.name || null);
      onClose();
      setSelectedValue(null);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedValue(null);
      setSearchTerm('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        New Recording for {personName}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Choose a template to structure the interview
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="template-search"
          label="Search templates"
          type="search"
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <List>
          {filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => (
              <ListItemButton
                key={template.id}
                selected={selectedValue === template.id}
                onClick={() => setSelectedValue(template.id)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {React.cloneElement(
                    template.icon as React.ReactElement<{ fontSize?: string }>,
                    { fontSize: 'medium' }
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.description}
                />
              </ListItemButton>
            ))
          ) : (
            <ListItem sx={{ justifyContent: 'center', py: 3 }}>
              <Typography color="text.secondary">
                No templates match your search.
              </Typography>
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={selectedValue === null}
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Edit Person Dialog Component ---
interface EditPersonDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: UpdatePersonInput) => Promise<void>;
  person: Person | null;
  loading?: boolean;
}

const EditPersonDialog: React.FC<EditPersonDialogProps> = ({
  open,
  onClose,
  onSave,
  person,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when person changes or dialog opens
  useEffect(() => {
    if (open && person) {
      setFormData({
        firstName: person.firstName || '',
        middleName: person.middleName || '',
        lastName: person.lastName || '',
        email: person.email || '',
        phone: person.phone || '',
        notes: person.notes || ''
      });
      setError(null);
    }
  }, [open, person]);

  const handleInputChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value
      }));
    };

  const handleSubmit = async () => {
    if (!person?.id) {
      setError('Person ID is required');
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setError(null);

      const updateInput: UpdatePersonInput = {
        id: person.id,
        firstName: formData.firstName.trim(),
        middleName: formData.middleName.trim() || undefined,
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        notes: formData.notes.trim() || undefined
      };

      await onSave(updateInput);
      onClose();
    } catch (err: any) {
      console.error('Failed to update person:', err);
      setError(err.message || 'Failed to update person');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Person Details</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                required
                fullWidth
                disabled={loading}
                autoFocus
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                required
                fullWidth
                disabled={loading}
              />
            </Grid>
          </Grid>

          <TextField
            label="Middle Name"
            value={formData.middleName}
            onChange={handleInputChange('middleName')}
            fullWidth
            disabled={loading}
            placeholder="Optional"
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            fullWidth
            disabled={loading}
            placeholder="Optional"
          />

          <TextField
            label="Phone"
            value={formData.phone}
            onChange={handleInputChange('phone')}
            fullWidth
            disabled={loading}
            placeholder="Optional"
          />

          <TextField
            label="Notes"
            value={formData.notes}
            onChange={handleInputChange('notes')}
            multiline
            rows={3}
            fullWidth
            disabled={loading}
            placeholder="Optional"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading || !formData.firstName.trim() || !formData.lastName.trim()
          }
          startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PersonDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { selectedUserRole, appUser } = useAppUser();
  const toast = useToast();

  const [person, setPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sessions, setSessions] = useState<TranscriptionSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Search state for different tabs
  const [sessionsSearchTerm, setSessionsSearchTerm] = useState('');
  const [sessionsSearchExpanded, setSessionsSearchExpanded] = useState(false);
  const [documentsSearchTerm, setDocumentsSearchTerm] = useState('');
  const [documentsSearchExpanded, setDocumentsSearchExpanded] = useState(false);
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [notesSearchExpanded, setNotesSearchExpanded] = useState(false);

  // Refs for click-outside functionality
  const sessionsSearchRef = useRef<HTMLDivElement>(null);
  const documentsSearchRef = useRef<HTMLDivElement>(null);
  const notesSearchRef = useRef<HTMLDivElement>(null);
  
  // Menu state for header actions
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Template and dialog state
  const [isSelectTemplateDialogOpen, setIsSelectTemplateDialogOpen] =
    useState(false);
  const [isEditPersonDialogOpen, setIsEditPersonDialogOpen] = useState(false);
  const [personActionMenuAnchorEl, setPersonActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editPersonLoading, setEditPersonLoading] = useState(false);

  // Initialize services
  const personService = React.useMemo(
    () => createPersonService(getAccessTokenSilently, getAccessTokenWithPopup),
    [getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const speakerManagementService = useMemo(
    () =>
      createSpeakerManagementService(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      ),
    [getAccessTokenSilently, getAccessTokenWithPopup]
  );

  // Transform templates for SelectTemplateDialog
  const templatesForSelection = useMemo(() => {
    if (templates.length === 0) return fallbackTemplatesForSelection;

    return templates.map((template) => ({
      id: template.id.toString(),
      name: template.name,
      description: template.description || 'No description available',
      icon: getTemplateIcon(template.category?.name || 'general')
    }));
  }, [templates]);

  // Handle starting new recording
  const handleStartNewRecording = useCallback(() => {
    setIsSelectTemplateDialogOpen(true);
  }, []);

  // Set header (Attio-style breadcrumb)
  useEffect(() => {
    const breadcrumbHeader = person ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          onClick={() => navigate('/people')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            transition: 'color 0.15s ease',
            '&:hover': { 
              color: theme.palette.text.primary 
            }
          }}
        >
          <Users size={16} weight="regular" />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'inherit'
            }}
          >
            People
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '14px',
            color: theme.palette.text.disabled,
            userSelect: 'none',
            mx: -0.25
          }}
        >
          /
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.primary,
            maxWidth: isMobile ? '180px' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {person.displayName}
        </Typography>
      </Box>
    ) : (
      'Person Details'
    );

    setHeaderText(breadcrumbHeader);
    
    // Contextual actions based on active tab
    let actions;
    
    if (activeTab === 'overview') {
      // Overview tab: Show Edit button
      actions = isMobile ? (
        <IconButton
          onClick={() => setIsEditPersonDialogOpen(true)}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          <EditIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setIsEditPersonDialogOpen(true)}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          Edit Details
        </Button>
      );
    } else if (activeTab === 'sessions') {
      // Recordings tab: Show New Recording button
      actions = isMobile ? (
        <IconButton
          onClick={handleStartNewRecording}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          <Plus size={18} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<Microphone />}
          onClick={handleStartNewRecording}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          New Recording
        </Button>
      );
    } else if (activeTab === 'documents') {
      // Documents tab: Show Upload Document button
      actions = isMobile ? (
        <IconButton
          onClick={() => {
            // TODO: Implement upload document functionality
            console.log('Upload document clicked');
          }}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          <Plus size={18} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<PlusCircle size={18} weight="regular" />}
          onClick={() => {
            // TODO: Implement upload document functionality
            console.log('Upload document clicked');
          }}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          Upload Document
        </Button>
      );
    } else if (activeTab === 'notes') {
      // Notes tab: Show New Note button
      actions = isMobile ? (
        <IconButton
          onClick={() => {
            // TODO: Implement new note functionality
            console.log('New note clicked');
          }}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          <Plus size={18} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<Note />}
          onClick={() => {
            // TODO: Implement new note functionality
            console.log('New note clicked');
          }}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          New Note
        </Button>
      );
    } else {
      // Default: Show generic New Item button with menu
      actions = isMobile ? (
        <IconButton
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          <Plus size={18} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<Plus />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          New Item
        </Button>
      );
    }
    
    setMenuActions(actions);

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [person, setHeaderText, setMenuActions, navigate, theme.palette.text.secondary, theme.palette.text.primary, handleStartNewRecording, isMobile, theme.palette.mode, activeTab]);

  // Load templates
  const loadTemplates = useCallback(async () => {
    if (!selectedUserRole?.companyId || !appUser?.id) return;

    try {
      const params: GetAllTemplatesParams = {
        userId: appUser.id,
        includeArchived: false
      };

      const templateData = await templateService.getAllTemplates(
        params,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      setTemplates(templateData);
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Use fallback templates if API fails
      setTemplates([]);
    }
  }, [
    selectedUserRole?.companyId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Handle template selection and session creation
  const handleTemplateSelected = async (
    templateId: string | null,
    templateName: string | null
  ) => {
    if (!person || !appUser?.id) {
      console.error('Missing person or user information');
      toast.showError('Unable to create recording session');
      return;
    }

    const personId = person.id;
    console.log(
      `Creating recording session for ${person.displayName} with template: ${
        templateName || 'Custom'
      }`
    );

    try {
      // Create session with person association
      const sessionId = await createSessionWithPerson({
        personId: personId,
        templateId: templateId ? Number(templateId) : undefined,
        templateName: templateName || undefined,
        userId: appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        navigate,
        speakerManagementService
      });

      if (sessionId) {
        toast.showSuccess(`Session created for ${person.displayName}`);
      } else {
        toast.showError('Failed to create recording session');
      }
    } catch (error) {
      console.error('Error creating recording session:', error);
      toast.showError('Failed to create recording session');
    }

    // Reset state
    setIsSelectTemplateDialogOpen(false);
  };

  // Handle editing person
  const handleEditPerson = async (input: UpdatePersonInput) => {
    try {
      setEditPersonLoading(true);

      const updatedPerson = await personService.updatePerson(input);

      // Update the local person state with the updated data
      setPerson(updatedPerson);

      toast.showSuccess(`${updatedPerson.displayName} updated`);
    } catch (error) {
      console.error('Failed to update person:', error);
      toast.showError('Failed to update person');
      throw error; // Re-throw to let the dialog handle it
    } finally {
      setEditPersonLoading(false);
    }
  };

  // Load templates when component mounts
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Load person data
  useEffect(() => {
    const loadPerson = async () => {
      if (!id || !selectedUserRole?.companyId) {
        setError('Missing person ID or company information');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const personData = await personService.getPersonById(
          id,
          selectedUserRole.companyId
        );

        if (personData) {
          setPerson(personData);
        } else {
          setError('Person not found');
        }
      } catch (err) {
        console.error('Failed to load person:', err);
        setError('Failed to load person details');
      } finally {
        setLoading(false);
      }
    };

    loadPerson();
  }, [id, selectedUserRole?.companyId, personService]);

  // Load sessions for the person (using real API data)
  const loadSessions = React.useCallback(async () => {
    if (!person?.id || !appUser?.id) {
      console.log(
        'PersonDetailPage: Cannot load sessions - missing person.id or appUser.id',
        { personId: person?.id, appUserId: appUser?.id }
      );
      return;
    }

    try {
      setLoadingSessions(true);
      console.log('PersonDetailPage: Loading sessions for person', person.id);

      // Get sessions filtered by person ID
      const paginatedResponse = await getAllTranscriptionSessions(
        {
          userId: appUser.id,
          pageNumber: 1,
          pageSize: 100, // Get a large number to capture all sessions
          sortBy: 'sessionstarttime',
          sortAscending: false,
          personId: person.id // Filter by the current person
        },
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      console.log(
        'PersonDetailPage: Loaded sessions for person',
        person.id,
        ':',
        paginatedResponse.sessions
      );
      setSessions(paginatedResponse.sessions);
    } catch (error) {
      console.error(
        'PersonDetailPage: Failed to load sessions for person',
        person.id,
        ':',
        error
      );
    } finally {
      setLoadingSessions(false);
    }
  }, [
    person?.id,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  // Load sessions immediately when person data is available
  React.useEffect(() => {
    if (person && sessions.length === 0 && !loadingSessions) {
      loadSessions();
    }
  }, [person, sessions.length, loadingSessions, loadSessions]);

  // Click outside handlers for search boxes
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sessionsSearchRef.current &&
        !sessionsSearchRef.current.contains(event.target as Node)
      ) {
        if (sessionsSearchExpanded && !sessionsSearchTerm) {
          setSessionsSearchExpanded(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sessionsSearchExpanded, sessionsSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        documentsSearchRef.current &&
        !documentsSearchRef.current.contains(event.target as Node)
      ) {
        if (documentsSearchExpanded && !documentsSearchTerm) {
          setDocumentsSearchExpanded(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [documentsSearchExpanded, documentsSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notesSearchRef.current &&
        !notesSearchRef.current.contains(event.target as Node)
      ) {
        if (notesSearchExpanded && !notesSearchTerm) {
          setNotesSearchExpanded(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notesSearchExpanded, notesSearchTerm]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPersonInitials = (person: Person) => {
    return `${person.firstName[0]}${person.lastName[0]}`.toUpperCase();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="calc(100vh - 120px)"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }} color="text.secondary">
          Loading person details...
        </Typography>
      </Box>
    );
  }

  if (error || !person) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error || 'Person not found'}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: `${theme.palette.background.paper} !important`
      }}
    >
      {/* Tabs */}
      <UnifiedTabsContainer>
        <UnifiedTabs
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'sessions', label: 'Recordings', count: sessions.length },
            { key: 'documents', label: 'Documents' },
            { key: 'notes', label: 'Notes' }
          ]}
          activeTab={activeTab}
          onTabChange={(key) => setActiveTab(key as string)}
        />
      </UnifiedTabsContainer>

      {/* Main Content Area */}
      <Box
        sx={{
          px: 0,
          pt: 0,
          pb: 0,
          flex: 1,
          overflow: 'auto',
          background: theme.palette.background.paper
        }}
      >
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Card sx={{ mb: 3, boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: '24px' }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 2,
                    textAlign: 'left'
                  }}
                >
                  Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: theme.palette.text.secondary,
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500,
                          textAlign: 'left'
                        }}
                      >
                        Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.primary,
                          textAlign: 'left'
                        }}
                      >
                        {person.displayName || '—'}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: theme.palette.text.secondary,
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500,
                          textAlign: 'left'
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.primary,
                          textAlign: 'left'
                        }}
                      >
                        {person.email || '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: theme.palette.text.secondary,
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500,
                          textAlign: 'left'
                        }}
                      >
                        First Record Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.primary,
                          textAlign: 'left'
                        }}
                      >
                        {person.createdAt ? formatDate(person.createdAt) : '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={6}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: theme.palette.text.secondary,
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500,
                          textAlign: 'left'
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.primary,
                          textAlign: 'left'
                        }}
                      >
                        {person.phone || '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: theme.palette.text.secondary,
                          mb: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500,
                          textAlign: 'left'
                        }}
                      >
                        Last Updated
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.primary,
                          textAlign: 'left'
                        }}
                      >
                        {person.updatedAt ? formatDate(person.updatedAt) : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <Box>
            {/* Clean Search Bar */}
            <Box sx={{ px: 3, py: 2, mb: 0 }}>
              <TextField
                fullWidth
                placeholder="Search recordings..."
                variant="outlined"
                size="small"
                value={sessionsSearchTerm}
                onChange={(e) => setSessionsSearchTerm(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '13px',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    height: 40,
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                    },
                    '&.Mui-focused': {
                      borderColor: '#6b7280',
                      boxShadow: 'none'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: sessionsSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSessionsSearchTerm('')}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {loadingSessions ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', py: 4, px: 3 }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : sessions.length > 0 ? (
              <Box sx={{ px: 3 }}>
                <Stack spacing={1}>
                  {sessions
                    .filter(
                      (session) =>
                        !sessionsSearchTerm ||
                        (session.title || 'Recording Session')
                          .toLowerCase()
                          .includes(sessionsSearchTerm.toLowerCase())
                    )
                    .map((session) => (
                      <Card
                        key={session.id}
                        onClick={() =>
                          sessionNavigationService.navigateToSession(
                            session.id,
                            navigate
                          )
                        }
                        sx={{
                          background: theme.palette.background.paper,
                          borderRadius: '6px',
                          boxShadow: 'none',
                          border: `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          transition: 'all 0.15s ease',
                          cursor: 'pointer',
                          textAlign: 'left',
                          '&:hover': {
                            borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.05)' 
                                : '#f3f4f6',
                              color: '#6b7280',
                              flexShrink: 0
                            }}
                          >
                            <MicIcon sx={{ fontSize: 16 }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {session.title || 'Recording Session'}
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', color: theme.palette.text.secondary }}
                            >
                              {formatDate(session.sessionStartTime)}
                            </Typography>
                          </Box>
                          {session.sessionEndTime ? null : (
                            <Chip
                              label="In Progress"
                              size="small"
                              sx={{
                                fontSize: '11px',
                                height: 20,
                                borderRadius: '4px',
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? 'rgba(245, 158, 11, 0.15)' 
                                  : '#fef3c7',
                                color: '#f59e0b',
                                border: 'none',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          )}
                        </Box>
                      </Card>
                    ))}
                </Stack>
              </Box>
            ) : (
              <Box sx={{ py: 8, color: theme.palette.text.secondary, px: 3 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, mb: 2 }}>
                  No sessions found
                </Typography>
                <Typography sx={{ fontSize: '14px', color: theme.palette.text.secondary, opacity: 0.7, mb: 3 }}>
                  This person hasn't participated in any sessions yet.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Plus />}
                  onClick={handleStartNewRecording}
                  sx={{
                    borderRadius: '6px',
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
                    }
                  }}
                >
                  Start First Recording
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <Box>
            {/* Search + Upload Bar */}
            <Box sx={{ px: 3, py: 2, mb: 0 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Search documents..."
                  variant="outlined"
                  size="small"
                  value={documentsSearchTerm}
                  onChange={(e) => setDocumentsSearchTerm(e.target.value)}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontSize: '13px',
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      height: 40,
                      '&:hover': {
                        borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                      },
                      '&.Mui-focused': {
                        borderColor: '#6b7280',
                        boxShadow: 'none'
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: documentsSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setDocumentsSearchTerm('')}>
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<PlusCircle size={16} weight="regular" />}
                  onClick={() => {
                    // TODO: Implement upload document functionality
                    console.log('Upload document clicked');
                  }}
                  sx={{
                    height: 40,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    px: 2,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : '#f9fafb',
                      borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                    }
                  }}
                >
                  Upload
                </Button>
              </Box>
            </Box>

            {/* Empty state */}
            <Box sx={{ px: 3 }}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : '#f3f4f6',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: '#9ca3af'
                }}>
                  <DescriptionIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography sx={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  color: theme.palette.text.primary,
                  mb: 0.5
                }}>
                  No documents yet
                </Typography>
                <Typography sx={{ 
                  fontSize: '13px', 
                  color: theme.palette.text.secondary
                }}>
                  Documents from recording sessions will appear here
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Card sx={{ mb: 3, boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: '24px' }}>
                {person.notes ? (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 2,
                        textAlign: 'left'
                      }}
                    >
                      AI Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: theme.palette.text.primary,
                        textAlign: 'left',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {person.notes}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '50%',
                      background: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : '#f3f4f6',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: '#9ca3af'
                    }}>
                      <NotesIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography sx={{ 
                      fontSize: '16px', 
                      fontWeight: 600, 
                      color: theme.palette.text.primary,
                      mb: 0.5
                    }}>
                      No notes yet
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '13px', 
                      color: theme.palette.text.secondary
                    }}>
                      AI-created notes from conversation analysis will appear here
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Select Template Dialog */}
      <SelectTemplateDialog
        open={isSelectTemplateDialogOpen}
        onClose={() => setIsSelectTemplateDialogOpen(false)}
        onSelect={handleTemplateSelected}
        templates={templatesForSelection}
        personName={person?.displayName}
      />

      {/* Edit Person Dialog */}
      <EditPersonDialog
        open={isEditPersonDialogOpen}
        onClose={() => setIsEditPersonDialogOpen(false)}
        onSave={handleEditPerson}
        person={person}
        loading={editPersonLoading}
      />

      {/* Person Action Menu */}
      <Menu
        anchorEl={personActionMenuAnchorEl}
        open={Boolean(personActionMenuAnchorEl)}
        onClose={() => setPersonActionMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleStartNewRecording();
            setPersonActionMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Plus size={20} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Start New Recording
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Header New Item Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.5)'
              : '0 2px 8px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            mt: 1
          }
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            handleStartNewRecording();
          }}
          sx={{
            py: 1.25,
            px: 2,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <ListItemIcon>
            <Microphone size={18} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            New Recording
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setActiveTab('documents');
          }}
          sx={{
            py: 1.25,
            px: 2,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <ListItemIcon>
            <File size={18} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            New Document
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setActiveTab('notes');
          }}
          sx={{
            py: 1.25,
            px: 2,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <ListItemIcon>
            <Note size={18} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            New Note
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PersonDetailPage;
